import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableContainer,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  CircularProgress,
  Link
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate, useLocation } from "react-router-dom";

const AdminProforma = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [proformas, setProformas] = useState([]);
  const [filteredProformas, setFilteredProformas] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadText, setDownloadText] = useState("Descargando proforma...");
  const [downloadError, setDownloadError] = useState("");

  const fetchProformas = useCallback(() => {
    fetch("/proformas/listar-proformas/", { credentials: "include" })
      .then((resp) => resp.json())
      .then((data) => {
        setProformas(data);
        setFilteredProformas(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchProformas();
  }, [fetchProformas]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchProformas();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchProformas]);

  useEffect(() => {
    if (location.state && location.state.created) {
      setSuccessMessage("Proforma creada con éxito.");
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleDownload = (proforma) => {
    setDownloadError("");
    setDownloadText(`Descargando ${proforma.identificacion_proforma}...`);
    setDownloadOpen(true);

    const url = `/proformas/descargar-proforma/${proforma.id}/?t=${Date.now()}`;

    fetch(url, { credentials: "include" })
      .then((resp) => {
        if (!resp.ok) throw new Error("No se pudo descargar la proforma.");
        return resp.blob();
      })
      .then((blob) => {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Proforma_${proforma.identificacion_proforma}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setDownloadOpen(false);
      })
      .catch(() => {
        setDownloadOpen(false);
        setDownloadError("Error al descargar la proforma. Intente nuevamente.");
      });
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);

    const filtered = proformas.filter((p) => {
      const id = ((p.identificacion_proforma || "") + "").toLowerCase();
      const cliente = ((p.cliente || "") + "").toLowerCase();
      const contacto = ((p.contacto || "") + "").toLowerCase();
      const elaborado = ((p.elaborado_por || "") + "").toLowerCase();

      return id.includes(value) || cliente.includes(value) || contacto.includes(value) || elaborado.includes(value);
    });

    setFilteredProformas(filtered);
    setPage(0);
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.removeItem("user_email");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("is_logged_in");
    navigate("/login");
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleSelect={handleSelect}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1, transition: "all 0.3s ease" }}>
        <TopBar open={open} setOpen={setOpen} setSelectedComponent={() => navigate("/admin-panel")} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3, gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
              onClick={() => navigate("/admin-panel/proformas/nueva")}
            >
              Crear Proforma
            </Button>
          </Box>

          <Paper sx={{ p: 3, borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", bgcolor: "#FFFFFF" }}>
            <Typography variant="h6" mb={2}>
              Proformas
            </Typography>

            <TextField
              label="Buscar Proforma"
              variant="outlined"
              fullWidth
              value={search}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />

            {filteredProformas.length === 0 ? (
              <Typography sx={{ color: "#666" }}>No hay proformas registradas.</Typography>
            ) : (
              <>
                <TableContainer>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>ID</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Cliente</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Contacto</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Estado</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Fecha</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Elaborado por</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Descargar</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredProformas
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((p) => (
                          <TableRow key={p.id}>
                            <TableCell>
                              <Link
                                component="button"
                                underline="hover"
                                sx={{ fontWeight: 600, color: "#1565C0" }}
                                onClick={() => navigate(`/admin-panel/proformas/facturacion/${p.id}`)}
                              >
                                {p.identificacion_proforma}
                              </Link>
                            </TableCell>
                            <TableCell>{p.cliente}</TableCell>
                            <TableCell>{p.contacto}</TableCell>
                            <TableCell>{p.estado_proforma}</TableCell>
                            <TableCell>{p.fecha}</TableCell>
                            <TableCell>{p.elaborado_por}</TableCell>

                            <TableCell>
                              <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload(p)}
                                sx={{
                                  textTransform: "none",
                                  borderColor: "#1565C0",
                                  color: "#1565C0",
                                  "&:hover": { borderColor: "#0D47A1", color: "#0D47A1" }
                                }}
                              >
                                Descargar
                              </Button>
                            </TableCell>

                            <TableCell>
                              <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/admin-panel/proformas/editar/${p.id}`)}
                                sx={{
                                  textTransform: "none",
                                  borderColor: "#1565C0",
                                  color: "#1565C0",
                                  "&:hover": { borderColor: "#0D47A1", color: "#0D47A1" }
                                }}
                              >
                                Editar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredProformas.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[10]}
                />
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog open={downloadOpen} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
            <CircularProgress size={26} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>{downloadText}</Typography>
              <Typography sx={{ color: "#666", fontSize: 13 }}>Por favor espere, no cierre esta ventana.</Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar
        open={!!downloadError}
        autoHideDuration={3500}
        onClose={() => setDownloadError("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{downloadError}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminProforma;
